import {
  ThemeBorderRadius,
  ThemeColors,
  ThemeFont,
  ThemeSpaces,
} from '../theme';
import { borderRadius, withTriangle } from './mixins';

const DEFAULT_BOX_SHADOW = `0px 2px 20px ${ThemeColors.COLOR_GREY2}`;

export const menuItemActive = {
  backgroundColor: ThemeColors.COLOR_RED,
  color: ThemeColors.COLOR_WHITE,
};

export const menuItemHover = {
  backgroundColor: ThemeColors.COLOR_RED6,
  color: ThemeColors.COLOR_WHITE,
};

export const menuItemPressed = {
  backgroundColor: ThemeColors.COLOR_RED7,
  color: ThemeColors.COLOR_WHITE,
  boxShadow: 'inset 0 1px 3px #640000',
};

const darkMenuItemState = {
  backgroundColor: ThemeColors.COLOR_GREY_DARK2,
  color: ThemeColors.COLOR_GREY_BLUISH7_4,
};

export const darkMenuItemActive = darkMenuItemState;
export const darkMenuItemHover = darkMenuItemState;
export const darkMenuItemPressed = darkMenuItemState;

const twoLinesSeparator = {
  position: 'relative',
  height: 0,
  margin: '1px 0 -1px 0',
  borderTop: `1px solid ${ThemeColors.COLOR_WHITE}`,
  '&::before': {
    content: '" "',
    position: 'absolute',
    top: -2,
    left: 0,
    right: 0,
    borderTop: `1px solid ${ThemeColors.COLOR_GREY8}`,
  },
  '&::after': {
    content: '" "',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    borderTop: `1px solid ${ThemeColors.COLOR_WHITE}`,
  },
};

const TRIANGLE_WIDTH = 18;
const TRIANGLE_HEIGHT = 12;
const SIDE_SPACE = ThemeSpaces.SPACE_NORM;

const placement = (direction, position, borderColor, backgroundColor) => ({
  ...withTriangle({
    width: TRIANGLE_WIDTH,
    height: TRIANGLE_HEIGHT,
    direction,
    borderWidth: 1,
    borderColor,
    backgroundColor,
    position,
  }),
});

export function dropdownWithTriangle({
  dropdownPrefix = 'ant-dropdown',
  menuSelector = '.ant-dropdown-menu',
  menuCustom = {},
  menuItemCustom = {},
  menuItemPrefix = 'ant-dropdown-menu',
  trianglePositionMultiplier = 1,
  triangleCustom = {},
  marginTop = 0,
  itemActive = menuItemActive,
  itemHover = menuItemHover,
  itemPressed = menuItemPressed,
  boxShadow = DEFAULT_BOX_SHADOW,
  borderColor = ThemeColors.COLOR_WHITE,
  backgroundColor = ThemeColors.COLOR_WHITE4,
} = {}) {
  const triangleOffset = TRIANGLE_WIDTH * trianglePositionMultiplier;

  const placements = {
    [`&.${dropdownPrefix}-placement-bottomRight`]: {
      [menuSelector]: placement(
        'up',
        { bottom: '100%', right: triangleOffset, ...triangleCustom },
        borderColor,
        backgroundColor
      ),
    },
    [`&.${dropdownPrefix}-placement-bottom, &.${dropdownPrefix}-placement-bottomCenter`]:
      {
        [menuSelector]: placement(
          'up',
          {
            bottom: '100%',
            right: '50%',
            transform: 'translateX(50%)',
            ...triangleCustom,
          },
          borderColor,
          backgroundColor
        ),
      },
    [`&.${dropdownPrefix}-placement-bottomLeft`]: {
      [menuSelector]: placement(
        'up',
        { bottom: '100%', left: triangleOffset, ...triangleCustom },
        borderColor,
        backgroundColor
      ),
    },
    [`&.${dropdownPrefix}-placement-topRight`]: {
      [menuSelector]: placement(
        'down',
        { top: '100%', right: triangleOffset, ...triangleCustom },
        borderColor,
        backgroundColor
      ),
    },
    [`&.${dropdownPrefix}-placement-top, &.${dropdownPrefix}-placement-topCenter`]:
      {
        [menuSelector]: placement(
          'down',
          {
            top: '100%',
            right: '50%',
            transform: 'translateX(50%)',
            ...triangleCustom,
          },
          borderColor,
          backgroundColor
        ),
      },
    [`&.${dropdownPrefix}-placement-topLeft`]: {
      [menuSelector]: placement(
        'down',
        { top: '100%', left: triangleOffset, ...triangleCustom },
        borderColor,
        backgroundColor
      ),
    },
  };

  return {
    ...placements,
    [menuSelector]: {
      marginTop: TRIANGLE_HEIGHT - 2 + marginTop,
      padding: 0,
      borderRadius: ThemeBorderRadius.BORDER_RADIUS_MD,
      boxShadow,
      fontFamily: ThemeFont.FONT_FAMILY3,
      overflow: 'visible',
      ...menuCustom,

      [`.${menuItemPrefix}-item, .${menuItemPrefix}-submenu-title`]: {
        display: 'flex',
        alignItems: 'stretch',
        height: 26,
        fontWeight: 'bold',
        margin: 0,
        padding: 0,
        ...menuItemCustom,
        ...borderRadius({ size: 0 }),

        '&:first-child': {
          '&, & > a': {
            ...borderRadius({
              size: ThemeBorderRadius.BORDER_RADIUS_MD,
              side: 'top',
            }),
          },
        },
        '&:last-child': {
          '&, & > a': {
            ...borderRadius({
              size: ThemeBorderRadius.BORDER_RADIUS_MD,
              side: 'bottom',
            }),
          },
        },
        '&:first-child:last-child': {
          '&, & > a': {
            borderRadius: ThemeBorderRadius.BORDER_RADIUS_MD,
          },
        },

        '> *': {
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: `0 ${SIDE_SPACE}px`,
        },
        '> * > *': {
          display: 'flex',
          alignItems: 'center',
          alignSelf: 'stretch',
          flex: 1,
          padding: `0 ${SIDE_SPACE}px`,
          margin: `0 -${SIDE_SPACE}px`,
        },
      },

      [`.${menuItemPrefix}-item`]: {
        '&, > a': {
          transition: 'none',
        },
        '&.active, &-selected, &-selected > a': {
          position: 'relative',
          zIndex: 1,
          ...itemActive,
        },
        '&:hover, &:not(.ant-menu-item-selected):hover, > a:hover': {
          position: 'relative',
          zIndex: 1,
          ...itemHover,
        },
        '&:active, > a:active': {
          position: 'relative',
          zIndex: 1,
          ...itemPressed,
        },
      },

      [`.${menuItemPrefix}-item-divider`]: {
        ...twoLinesSeparator,
        overflow: 'visible',
        margin: `0 ${SIDE_SPACE}px -1px ${SIDE_SPACE}px`,
        backgroundColor: 'transparent',
        zIndex: 0,
      },
    },
  };
}
